import { createContext, useContext } from "react";

/**
 * What a page needs to know about the nav chrome.
 *
 * The panel itself is mounted once, by the app layout route, and outlives
 * every page. The page only has to reserve the right amount of horizontal
 * space for it — which depends on whether the user pinned it open, and on
 * whether we are in rail or drawer mode. That is the whole contract.
 */
export type SidebarChromeValue = {
  /** Desktop only: the panel is pinned open, so the page must shift over. */
  pinned: boolean;
  /** Rail/drawer switch. In drawer mode the page reserves nothing. */
  isMobile: boolean;
};

export const SidebarChromeContext = createContext<SidebarChromeValue | null>(null);

let warned = false;

/**
 * Read the chrome state from inside a page.
 *
 * Falls back rather than throwing: a `<Sidebar>` rendered outside the layout
 * route is a routing mistake, not a reason to blank the screen. In dev it says
 * exactly which fix is missing, once, instead of on every render.
 */
export const useSidebarChrome = (): SidebarChromeValue => {
  const ctx = useContext(SidebarChromeContext);
  if (ctx) return ctx;

  if (process.env.NODE_ENV !== "production" && !warned) {
    warned = true;
    // eslint-disable-next-line no-console
    console.warn(
      "[Sidebar] rendered outside <AppLayout>, so the navigation panel is missing. " +
        "Move this route inside the AppLayout route group in src/routes/Routes.tsx."
    );
  }
  return { pinned: false, isMobile: false };
};
